import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

export interface ClassDescriptionEditorProps {
  html: string;
  onHtmlChange: (html: string) => void;
  /** When true, content is shown without editing (matches view-only permissions). */
  readOnly?: boolean;
}

const ClassDescriptionEditor: React.FC<ClassDescriptionEditorProps> = ({
  html,
  onHtmlChange,
  readOnly = false,
}) => {
  const editor = useEditor({
    extensions: [StarterKit],
    editable: !readOnly,
    content: html || '<p></p>',
    onUpdate: ({ editor }) => {
      const nextHtml = editor.getHTML();
      onHtmlChange(nextHtml);
    },
  });

  // Keep external html prop and internal editor state in sync
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const target = html || '<p></p>';
    if (current !== target) {
      editor.commands.setContent(target, { emitUpdate: false });
    }
  }, [html, editor]);

  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [readOnly, editor]);

  if (!editor) return null;

  return (
    <div className="class-description-editor-wrap">
      <div className="class-description-editor">
        {!readOnly && (
        <div className="class-description-editor-toolbar">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn btn-outline-secondary${editor.isActive('bold') ? ' active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleBold().run()}
              title="Bold"
            >
              <i className="bi bi-type-bold" />
            </button>
            <button
              type="button"
              className={`btn btn-outline-secondary${editor.isActive('italic') ? ' active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              title="Italic"
            >
              <i className="bi bi-type-italic" />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              title="Heading"
            >
              <i className="bi bi-type-h3" />
            </button>
            <button
              type="button"
              className={`btn btn-outline-secondary${editor.isActive('bulletList') ? ' active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              title="Bullet list"
            >
              <i className="bi bi-list-ul" />
            </button>
            <button
              type="button"
              className={`btn btn-outline-secondary${editor.isActive('orderedList') ? ' active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              title="Numbered list"
            >
              <i className="bi bi-list-ol" />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().undo().run()}
              title="Undo"
            >
              <i className="bi bi-arrow-counterclockwise" />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => editor.chain().focus().redo().run()}
              title="Redo"
            >
              <i className="bi bi-arrow-clockwise" />
            </button>
          </div>
        </div>
        )}
        <div className="class-description-editor-input-wrapper">
          <EditorContent editor={editor} className="class-description-editor-content" />
        </div>
      </div>
    </div>
  );
};

export default ClassDescriptionEditor;